import React, { useState, useEffect, useRef, useCallback } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { CompetitionCount } from "./SourcesFilters";

interface CompetitionAutocompleteProps {
  competitions: CompetitionCount[];
  selectedCompetitionId: string;
  onCompetitionChange: (competitionId: string) => void;
  totalSources: number;
}

export const CompetitionAutocomplete: React.FC<
  CompetitionAutocompleteProps
> = ({ competitions, selectedCompetitionId, onCompetitionChange, totalSources }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCompetition = competitions.find(
    (c) => c.id === selectedCompetitionId
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  const filteredCompetitions = competitions.filter((competition) => {
    const query = searchQuery.toLowerCase();
    const matchesName = competition.name?.toLowerCase().includes(query);
    const matchesCode = competition.code?.toLowerCase().includes(query);
    return matchesName || matchesCode;
  });

  const highlightText = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const handleCompetitionClick = (competitionId: string) => {
    onCompetitionChange(competitionId);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    onCompetitionChange("");
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[200px] text-left flex items-center justify-between"
      >
        <span
          className={
            selectedCompetition ? "text-neutral-900" : "text-neutral-500"
          }
        >
          {selectedCompetition
            ? `${selectedCompetition.name} (${selectedCompetition.count})`
            : `Wszystkie ${totalSources > 0 ? `(${totalSources})` : ""}`}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-neutral-200 rounded-lg shadow-lg z-[60] max-h-96 overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="sticky top-0 bg-white border-b border-neutral-200 p-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsOpen(false);
                    setSearchQuery("");
                  }
                }}
                placeholder="Szukaj konkursu..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {/* Clear selection option */}
            {selectedCompetitionId && (
              <button
                onClick={handleClear}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 text-primary-600 font-medium"
              >
                Wszystkie konkursy
              </button>
            )}

            {filteredCompetitions.length === 0 ? (
              <div className="text-center text-neutral-500 py-4">
                Nie znaleziono konkursów
              </div>
            ) : (
              filteredCompetitions.map((competition) => (
                <button
                  key={competition.id}
                  onClick={() => handleCompetitionClick(competition.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-b-0 ${
                    competition.id === selectedCompetitionId
                      ? "bg-primary-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-neutral-900">
                      {highlightText(competition.name, searchQuery)}
                    </div>
                    <span className="text-xs font-medium text-primary-600 ml-2 whitespace-nowrap">
                      {competition.count}{" "}
                      {competition.count === 1 ? "źródło" : "źródeł"}
                    </span>
                  </div>
                  {competition.code && (
                    <div className="text-xs text-neutral-500 mt-1">
                      {highlightText(competition.code, searchQuery)}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionAutocomplete;
